import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { supabase } from '../services/supabase';
import ConnectyCube from 'connectycube';
import { getLoginTranslation, detectLanguage } from './loginTranslations';
import LanguageToggle from '../components/LanguageToggle';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [lang, setLang] = useState(detectLanguage());
  const [t, setT] = useState(getLoginTranslation(detectLanguage()));

  useEffect(() => {
    setT(getLoginTranslation(lang));
    localStorage.setItem('preferredLanguage', lang);
  }, [lang]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Supabase client check
      if (!supabase) {
        setError(t.errors.serviceSetup);
        return;
      }

      // 1. Supabase 로그인
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (authError) {
        if (authError.message.includes('Email not confirmed')) {
          setError(t.errors.emailNotConfirmed);
        } else if (authError.message.includes('Invalid login credentials')) {
          setError(t.errors.invalidCredentials);
        } else {
          setError(authError.message);
        }
        return;
      }

      if (data.user) {
        // 2. ConnectyCube 로그인 시도 (선택사항 - 환경변수가 설정된 경우만)
        if (process.env.REACT_APP_CUBE_APP_ID && process.env.REACT_APP_CUBE_AUTH_KEY) {
          try {
            const CB: any = ConnectyCube;
            await CB.createSession();
            await CB.login({
              email: email,
              password: password
            });
            
            // 채팅 연결 (선택사항)
            const users: any = await CB.users.get({ email });
            if (users.items && users.items.length > 0) {
              const cubeUser = users.items[0];
              await CB.chat.connect({
                userId: cubeUser.id,
                password: password
              });
            }
          } catch (cubeError) {
            console.warn('ConnectyCube 로그인 실패 (선택적):', cubeError);
            // ConnectyCube 실패해도 Supabase 로그인은 성공
          }
        }

        // 3. 대시보드로 이동
        navigate('/dashboard');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setError(t.errors.loginError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center px-4">
      <div className="absolute top-4 right-4">
        <LanguageToggle currentLang={lang} onLanguageChange={setLang} />
      </div>
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="mb-6">
            <img 
              src="/aivuebiz_logo.png" 
              alt="AivueBiz Logo" 
              className="h-16 w-auto object-contain mx-auto"
            />
          </div>
          <p className="text-gray-600">{t.header.tagline}</p>
        </div>

        {/* Login Card */}
        <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">{t.form.title}</h2>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-6">
            {/* Email Input */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                {t.form.email.label}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                placeholder={t.form.email.placeholder}
                required
                disabled={loading}
              />
            </div>

            {/* Password Input */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                {t.form.password.label}
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                  placeholder={t.form.password.placeholder}
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Forgot Password Link */}
            <div className="text-right">
              <a 
                href="/download#reset-password" 
                className="text-sm text-primary-600 hover:text-primary-700 transition-colors"
              >
                {t.form.forgotPassword}
              </a>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3 px-6 rounded-xl font-semibold transition-all duration-300 ${
                loading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-primary-600 to-primary-700 text-white hover:shadow-xl hover:scale-105'
              }`}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t.form.submitting}
                </span>
              ) : (
                t.form.submit
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">{t.signup.noAccount}</span>
            </div>
          </div>

          {/* Signup Guide */}
          <div className="text-center space-y-4">
            <p className="text-gray-600" style={{ whiteSpace: 'pre-line' }}>
              {t.signup.description}
            </p>
            <Link
              to="/download"
              className="inline-flex items-center justify-center w-full py-3 px-6 border-2 border-primary-600 text-primary-600 rounded-xl font-semibold hover:bg-primary-50 transition-all duration-300"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
              </svg>
              {t.signup.downloadApp}
            </Link>
            <p className="text-xs text-gray-500">
              {t.signup.platforms}
            </p>
          </div>
        </div>

        {/* Footer Info */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>
            {t.footer.agreement}{' '}
            <Link to="/terms" className="text-primary-600 hover:text-primary-700">{t.footer.terms}</Link>{' '}
            {t.footer.and}{' '}
            <Link to="/privacy" className="text-primary-600 hover:text-primary-700">{t.footer.privacy}</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;